import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const NotFound = lazy(() => import('../pages/NotFound'));
const PricingPage = lazy(() => import('../pages/pricing/page'));
const TestersPage = lazy(() => import('../pages/testers/page'));
const MethodologyPage = lazy(() => import('../pages/methodology/page'));
const ThankYouPage = lazy(() => import('../pages/thank-you/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const OurProcessPage = lazy(() => import('../pages/our-process/page'));
const TestingProgramsPage = lazy(() => import('../pages/testing-programs/page'));
const InvestorSpotlightPage = lazy(() => import('../pages/investor-spotlight/page'));
const BecomeATesterPage = lazy(() => import('../pages/become-a-tester/page'));
const TesterApplicationPage = lazy(() => import('../pages/tester-application/page'));
const ClientPortalPage = lazy(() => import('../pages/client-portal/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const PricingQuotePage = lazy(() => import('../pages/pricing-quote/page'));
const TesterTermsPage = lazy(() => import('../pages/tester-terms/page'));
const CreateRoundPage = lazy(() => import('../pages/client-portal/create-round/page'));
const SampleReportPage = lazy(() => import('../pages/sample-report/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const CaseStudiesPage = lazy(() => import('../pages/case-studies/page'));
const TeamPage = lazy(() => import('../pages/team/page'));
const FounderDashboardPage = lazy(() => import('../pages/founder-dashboard/page'));
const TesterDashboardPage = lazy(() => import('../pages/tester-dashboard/page'));
const AppUploadPage = lazy(() => import('../pages/app-upload/page'));
const PassportCenterPage = lazy(() => import('../pages/passport-center/page'));
const AppPassportViewerPage = lazy(() => import('../pages/app-passport-viewer/page'));
const CreditScorePage = lazy(() => import('../pages/credit-score/page'));
const ValidationReportPage = lazy(() => import('../pages/validation-report/page'));
const FounderRegisterPage = lazy(() => import('../pages/founder-register/page'));
const TesterTrainingPage = lazy(() => import('../pages/tester-training/page'));
const PaymentConfirmationPage = lazy(() => import('../pages/payment-confirmation/page'));
const ResultsReadyPage = lazy(() => import('../pages/results-ready/page'));
const RefundPolicyPage = lazy(() => import('../pages/refund-policy/page'));
const Tier1QuickPulse = lazy(() => import('../pages/tier1-quick-pulse/page'));
const Tier2MarketReadiness = lazy(() => import('../pages/tier2-market-readiness/page'));
const Tier3CompetitiveIntelligence = lazy(() => import('../pages/tier3-competitive-intelligence/page'));
const Tier4SuccessBlueprint = lazy(() => import('../pages/tier4-success-blueprint/page'));
const Tier5FullSuccessIntelligence = lazy(() => import('../pages/tier5-full-success-intelligence/page'));
const ForInvestorsPage = lazy(() => import('../pages/for-investors/page'));
const AppWarsPage = lazy(() => import('../pages/app-wars/page'));
const FAQPage = lazy(() => import('../pages/faq/page'));
const LazyTier5FullSuccessIntelligence = lazy(() => import('../pages/tier5-full-success-intelligence/page'));
const LazyHowItWorks = lazy(() => import('../pages/how-it-works/page'));
const LazyForInvestors = lazy(() => import('../pages/for-investors/page'));
const LazyAppWars = lazy(() => import('../pages/app-wars/page'));
const LazyContactPage = lazy(() => import('../pages/contact/page'));
const LazyCheckoutPage = lazy(() => import('../pages/checkout/page'));
const SubmitAppPage = lazy(() => import('../pages/submit-app/page'));
const AddOnsPage = lazy(() => import('../pages/add-ons/page'));
const TierComparisonPage = lazy(() => import('../pages/tier-comparison/page'));
const IntelligenceFrameworkPage = lazy(() => import('../pages/intelligence-framework/page'));

// Create a wrapper component to handle Suspense
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div>Loading...</div>}>
    {children}
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: <LazyWrapper><HomePage /></LazyWrapper>,
  },
  {
    path: '/our-process',
    element: <LazyWrapper><OurProcessPage /></LazyWrapper>,
  },
  {
    path: '/testing-programs',
    element: <LazyWrapper><TestingProgramsPage /></LazyWrapper>,
  },
  {
    path: '/investor-spotlight',
    element: <LazyWrapper><InvestorSpotlightPage /></LazyWrapper>,
  },
  {
    path: '/become-a-tester',
    element: <LazyWrapper><BecomeATesterPage /></LazyWrapper>,
  },
  {
    path: '/tester-application',
    element: <LazyWrapper><TesterApplicationPage /></LazyWrapper>,
  },
  {
    path: '/client-portal',
    element: <LazyWrapper><ClientPortalPage /></LazyWrapper>,
  },
  {
    path: '/client-portal/create-round',
    element: <LazyWrapper><CreateRoundPage /></LazyWrapper>,
  },
  {
    path: '/contact',
    element: <LazyWrapper><LazyContactPage /></LazyWrapper>,
  },
  {
    path: '/checkout',
    element: <LazyWrapper><LazyCheckoutPage /></LazyWrapper>,
  },
  {
    path: '/pricing',
    element: <LazyWrapper><PricingPage /></LazyWrapper>,
  },
  {
    path: '/pricing-quote',
    element: <LazyWrapper><PricingQuotePage /></LazyWrapper>,
  },
  {
    path: '/sample-report',
    element: <LazyWrapper><SampleReportPage /></LazyWrapper>,
  },
  {
    path: '/about',
    element: <LazyWrapper><AboutPage /></LazyWrapper>,
  },
  {
    path: '/case-studies',
    element: <LazyWrapper><CaseStudiesPage /></LazyWrapper>,
  },
  {
    path: '/testers',
    element: <LazyWrapper><TestersPage /></LazyWrapper>,
  },
  {
    path: '/methodology',
    element: <LazyWrapper><MethodologyPage /></LazyWrapper>,
  },
  {
    path: '/intelligence-framework',
    element: <LazyWrapper><IntelligenceFrameworkPage /></LazyWrapper>,
  },
  {
    path: '/thank-you',
    element: <ThankYouPage />,
  },
  {
    path: '/terms',
    element: <TermsPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/tester-terms',
    element: <LazyWrapper><TesterTermsPage /></LazyWrapper>,
  },
  {
    path: '/founder-dashboard',
    element: <LazyWrapper><FounderDashboardPage /></LazyWrapper>
  },
  {
    path: '/tester-dashboard',
    element: <LazyWrapper><TesterDashboardPage /></LazyWrapper>
  },
  {
    path: '/app-upload',
    element: <LazyWrapper><AppUploadPage /></LazyWrapper>
  },
  {
    path: '/passport-center',
    element: <LazyWrapper><PassportCenterPage /></LazyWrapper>
  },
  {
    path: '/app-passport-viewer',
    element: <LazyWrapper><AppPassportViewerPage /></LazyWrapper>
  },
  {
    path: '/credit-score',
    element: <LazyWrapper><CreditScorePage /></LazyWrapper>
  },
  {
    path: '/validation-report',
    element: <LazyWrapper><ValidationReportPage /></LazyWrapper>
  },
  {
    path: '/founder-register',
    element: <LazyWrapper><FounderRegisterPage /></LazyWrapper>
  },
  {
    path: '/tester-training',
    element: <LazyWrapper><TesterTrainingPage /></LazyWrapper>
  },
  {
    path: '/payment-confirmation',
    element: <LazyWrapper><PaymentConfirmationPage /></LazyWrapper>
  },
  {
    path: '/results-ready',
    element: <LazyWrapper><ResultsReadyPage /></LazyWrapper>
  },
  {
    path: '/refund-policy',
    element: <LazyWrapper><RefundPolicyPage /></LazyWrapper>
  },
  {
    path: '/tier1-quick-pulse',
    element: <LazyWrapper><Tier1QuickPulse /></LazyWrapper>,
  },
  {
    path: '/tier2-market-readiness',
    element: <LazyWrapper><Tier2MarketReadiness /></LazyWrapper>,
  },
  {
    path: '/tier3-competitive-intelligence',
    element: <LazyWrapper><Tier3CompetitiveIntelligence /></LazyWrapper>,
  },
  {
    path: '/tier4-success-blueprint',
    element: <LazyWrapper><Tier4SuccessBlueprint /></LazyWrapper>,
  },
  {
    path: '/tier5-full-success-intelligence',
    element: <LazyWrapper><Tier5FullSuccessIntelligence /></LazyWrapper>,
  },
  {
    path: '/submit-app',
    element: <LazyWrapper><SubmitAppPage /></LazyWrapper>,
  },
  {
    path: '/add-ons',
    element: <LazyWrapper><AddOnsPage /></LazyWrapper>,
  },
  {
    path: '/tier-comparison',
    element: <LazyWrapper><TierComparisonPage /></LazyWrapper>,
  },
  {
    path: '/how-it-works',
    element: <LazyWrapper><LazyHowItWorks /></LazyWrapper>,
  },
  {
    path: '/team',
    element: <LazyWrapper><TeamPage /></LazyWrapper>,
  },
  {
    path: '/for-investors',
    element: <ForInvestorsPage />
  },
  {
    path: '/app-wars',
    element: <AppWarsPage />
  },
  {
    path: '/faq',
    element: <LazyWrapper><FAQPage /></LazyWrapper>,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
